import { useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../hooks/useConfirm';
import { useBusy } from '../../hooks/useBusy';
import { useAfterWrite } from '../../hooks/useAfterWrite';
import GenderBadge from '../shared/GenderBadge';
import ModalBackdrop from '../shared/ModalBackdrop';
import ModalHeader from '../shared/ModalHeader';
import ModalBody from '../shared/ModalBody';
import ModalFooter from '../shared/ModalFooter';
import { useI18n } from '../../i18n';
import { useLabels, countLabel, successText } from '../../i18n/labels';
import { personName } from '../../utils/personName';
import type { OpenRingPair } from '../../types';
import { BADGE_XS } from '../shared/badgeSize';

/** Display shape for one corner in the open-ring preview — backend
 *  returns a fuller view than the submit DTO needs. */
interface OpenRingCorner {
  registration_id: string;
  first_name: string;
  last_name: string;
  age_group?: string;
  weight_class?: string;
  gender?: string;
  team_name?: string;
  experience_bouts?: number;
}
interface OpenRingPreviewPair {
  red: OpenRingCorner;
  blue: OpenRingCorner;
}
export interface OpenRingPreviewData {
  pairs: (OpenRingPair & OpenRingPreviewPair)[];
  unmatched: OpenRingCorner[];
  total: number;
}

interface Props {
  pairs: OpenRingPreviewData;
  tournamentId: string;
  onClose: () => void;
  /** The write committed (#664): the parent closes the modal and reads the
   *  bouts again. The read rejects when it fails. */
  onConfirm: () => Promise<unknown>;
}

export default function OpenRingMatchPreviewModal({ pairs, tournamentId, onClose, onConfirm }: Props) {
  const { showError } = useToast();
  const { confirm } = useConfirm();
  const afterWrite = useAfterWrite();
  const { t, lang } = useI18n();
  const { boutCount, createBoutsLabel } = useLabels();
  // #605, #664 — the lock of Create: a second tap sends no second write.
  const creating = useBusy();

  async function create() {
    if (creating.busy) return;
    if (!(await confirm({
      message: t.tournament.createChallengeBoutsConfirm.replace('{n}', String(pairs.pairs.length)),
      confirmLabel: createBoutsLabel(pairs.pairs.length),
    }))) return;
    await creating.run(async () => {
      try {
        const { openRingConfirm } = await import('../../api/client');
        const res = await openRingConfirm(tournamentId, pairs.pairs);
        const skipped = res.data.skipped ? t.success.OPEN_RING_SKIPPED.replace('{skipped}', String(res.data.skipped)) : '';
        // #664 — the write committed: a failed read of the bouts is a warning (#419), not an error.
        await afterWrite({
          source: 'OpenRingMatchPreviewModal:create',
          success: successText(res.data) + skipped,
          warnings: res.data.warnings,
          refresh: onConfirm,
        });
      } catch (err) { showError(err); }
    });
  }

  // #552 — the shared panel: the Create button stays at the bottom of the
  // dialog, and it is off when there is no pair (it said "Create 0 Bouts").
  return (
    <ModalBackdrop onClose={onClose} maxWidth="lg" layout="panel" ariaLabel={t.openRingMatch.title}>
      <ModalHeader
        title={t.openRingMatch.title}
        subtitle={<>
          {countLabel(lang, pairs.pairs.length, { one: t.openRingMatch.summaryOne, few: t.openRingMatch.summaryFew, other: t.openRingMatch.summaryOther }).replace('{total}', String(pairs.total))}
          {pairs.unmatched.length > 0 && t.openRingMatch.unmatched.replace('{n}', String(pairs.unmatched.length))}
        </>}
      />
      <ModalBody>
        <div className="space-y-2 mb-4">
          {pairs.pairs.map((p, i) => (
            <div key={i} className="p-2 rounded-lg border border-gray-200 text-xs">
              <div className="flex items-center gap-1 mb-1">
                <span className={`badge badge-gray ${BADGE_XS}`}>{p.red.age_group}</span>
                <span className={`badge badge-amber ${BADGE_XS}`}>{p.red.weight_class}</span>
                <GenderBadge gender={(p.red.gender || '').toLowerCase()} short size="xs" />
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-red-700 truncate">{personName(p.red.first_name, p.red.last_name)}</div>
                  <div className="text-[10px] text-gray-400">{p.red.team_name} · {boutCount(p.red.experience_bouts ?? 0)}</div>
                </div>
                <span className="text-gray-400 shrink-0">{t.common.vs}</span>
                <div className="flex-1 min-w-0 text-right">
                  <div className="font-medium text-blue-700 truncate">{personName(p.blue.first_name, p.blue.last_name)}</div>
                  <div className="text-[10px] text-gray-400">{p.blue.team_name} · {boutCount(p.blue.experience_bouts ?? 0)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {pairs.unmatched.length > 0 && (
          <div className="mb-4">
            <div className="text-xs font-semibold text-gray-400 uppercase mb-1">{t.openRingMatch.unmatchedTitle.replace('{n}', String(pairs.unmatched.length))}</div>
            <div className="flex flex-wrap gap-1">
              {pairs.unmatched.map(a => (
                <span key={a.registration_id} className="text-[10px] bg-gray-50 border border-gray-200 rounded-sm px-1.5 py-0.5">
                  {personName(a.first_name, a.last_name)} ({a.team_name})
                </span>
              ))}
            </div>
          </div>
        )}

      </ModalBody>
      <ModalFooter
        onSave={create}
        busy={creating.busy}
        saveLabel={createBoutsLabel(pairs.pairs.length)}
        saveDisabled={pairs.pairs.length === 0}
        onCancel={onClose}
      />
    </ModalBackdrop>
  );
}
